import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import {
  createReportFile,
  fetchApprovalById,
  fetchReopenById,
  fetchReportById,
} from '../../../api/functionApi';
import { useAxiosInterceptor } from '../../../api/wrappers';
import { useReportFilesLasted } from '../../../api/hooks/useRanking';
import ReportEditorModal from '../../report/listaDriverPage/ranking/ReportEditorModal';
import type { ReportStato } from '../../../api/apiTypes';

interface Props {
  reportId:       string;
  /** True quando la /validate ritorna status = 'OK' (tutti i driver generati). */
  validationOk:   boolean;
}

const APPROVED_STATES: ReportStato[] = ['approved', 'drafted', 'completed'];

const ValidateDriversButton = ({ reportId, validationOk }: Props) => {
  // Registra il bearer token prima delle fetch (evita 401 al primo mount).
  useAxiosInterceptor();
  const [reportStatus, setReportStatus] = useState<ReportStato | null>(null);
  const [busy, setBusy] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const { file, fileLoading, fileError, refetch: refetchFile } = useReportFilesLasted(reportId);
  const [localFile, setLocalFile] = useState<any | undefined>(file);
  const [fileFetched, setFileFetched] = useState(false);

  useEffect(() => { setLocalFile(file); }, [file]);

  // Segna il primo fetch del file come completato (successo con dati o errore),
  // per evitare il flash "Genera Report" -> "Visualizza Report" prima che il
  // hook sappia se esiste gia' un file per il report.
  useEffect(() => {
    if (!fileLoading && (file !== undefined || fileError !== null)) {
      setFileFetched(true);
    }
  }, [fileLoading, file, fileError]);

  // Fetch iniziale dello stato del report (per capire se e' gia' approvato).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchReportById(reportId);
        if (!cancelled) setReportStatus(r.status);
      } catch (err) {
        console.error('[CR validate] fetch report status fallito:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [reportId]);

  const isApproved = reportStatus != null && APPROVED_STATES.includes(reportStatus);

  // Se la validate non e' ancora OK non mostro niente (il bottone appare solo
  // quando i 9 driver sono stati generati e le fonti sono tutte parsate).
  if (!validationOk) return null;

  // Non renderizzare finche' reportStatus non e' stato caricato: altrimenti
  // c'e' un flash iniziale con "Valida Driver" prima di sostituirlo con
  // "Modifica Driver" + "Genera/Visualizza Report" quando il report e' gia'
  // approvato. Meglio niente per un istante che una UI incoerente.
  if (reportStatus === null) return null;

  // Se il report e' approvato aspetto anche il fetch del latest file, cosi'
  // scelgo la label giusta ("Visualizza Report" vs "Genera Report") senza
  // farla lampeggiare.
  if (isApproved && !fileFetched) return null;

  const handleValidate = async () => {
    const result = await Swal.fire({
      title: 'Sei sicuro di voler provvedere?',
      html: `
        <div style="max-width: 550px; text-align: center; margin: 0 auto; color: #374151; line-height: 1.5;">
          <p>
            I driver che stai per validare sono stati generati da un sistema di intelligenza artificiale.
            Ti invitiamo a verificarne l'accuratezza e l'idoneità prima di confermarne la validazione.
          </p>
          <p style="margin-top: 10px;">
            Procedendo, dichiari di aver esaminato i contenuti e di assumerti la responsabilità per il loro utilizzo.
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Conferma validazione',
      cancelButtonText: 'Annulla',
      reverseButtons: true,
      customClass: {
        confirmButton: 'bg-textPrimary text-white hover:bg-textSecondary px-4 py-2 rounded ml-2',
        cancelButton:  'bg-textSecondary text-white hover:bg-textPrimary px-4 py-2 rounded',
        actions:       'flex justify-between gap-2',
      },
    });

    if (!result.isConfirmed) return;

    setBusy(true);
    Swal.fire({
      title: 'Validazione in corso...',
      text: 'Attendere qualche secondo',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      await fetchApprovalById(reportId);
      setReportStatus('approved');
      Swal.fire({
        icon: 'success',
        title: 'Validazione completata!',
        text: 'I driver sono stati validati correttamente.',
        confirmButtonColor: '#374151',
      });
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Validazione fallita',
        text: err?.message ?? 'Riprova.',
        confirmButtonColor: '#374151',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleReopen = async () => {
    const result = await Swal.fire({
      title: 'Riaprire il report?',
      text: 'Sei sicuro di voler riaprire questo report?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sì, riapri',
      cancelButtonText: 'Annulla',
      reverseButtons: true,
      confirmButtonColor: '#374151',
      cancelButtonColor: '#9CA3AF',
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Riapertura in corso...',
      text: 'Attendere il completamento.',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    setBusy(true);
    try {
      await fetchReopenById(reportId);
      Swal.close();
      setReportStatus('edited');
      setLocalFile(undefined);
      Swal.fire({
        icon: 'success',
        title: 'Report riaperto!',
        text: 'Il report è stato riaperto correttamente.',
        confirmButtonColor: '#374151',
      });
    } catch (err: any) {
      Swal.close();
      Swal.fire('⚠️', err?.message || 'Errore durante la riapertura del report.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateReport = async () => {
    const result = await Swal.fire({
      title: 'Generare il report?',
      text: 'Questa operazione potrebbe richiedere alcuni minuti.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sì, genera',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#374151',
      cancelButtonColor: '#9CA3AF',
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Generazione in corso...',
      text: 'Attendere il completamento.',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    setBusy(true);
    try {
      const newFile = await createReportFile(reportId);
      if (newFile) setLocalFile(newFile);
      Swal.close();
      setShowEditor(true);
      Swal.fire({
        title: 'Completato!',
        text: 'Il report è stato generato correttamente.',
        icon: 'success',
        confirmButtonColor: '#374151',
      });
    } catch (err: any) {
      Swal.close();
      Swal.fire({ title: 'Errore', text: err?.message ?? 'Impossibile generare il report.', icon: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleViewReport = () => {
    if (!localFile) {
      Swal.fire({
        title: 'File non disponibile',
        text: 'Non è stato trovato alcun file da visualizzare.',
        icon: 'warning',
      });
      return;
    }
    setShowEditor(true);
  };

  const handleCloseEditor = async () => {
    setShowEditor(false);
    await refetchFile();
    try {
      const updated = await fetchReportById(reportId);
      setReportStatus(updated.status);
    } catch (err) {
      console.error('[CR] refetch report status dopo editor fallito:', err);
    }
  };

  const hasFile = !!localFile;

  return (
    <>
      <div className="flex justify-end gap-2">
        {!isApproved && (
          <button
            onClick={handleValidate}
            disabled={busy}
            className="bg-textPrimary text-white hover:bg-textSecondary px-4 py-2 rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Valida Driver
          </button>
        )}

        {isApproved && (
          <>
            <button
              onClick={handleReopen}
              disabled={busy}
              className="bg-white text-textPrimary border border-textPrimary/30 hover:bg-textPrimary/5 px-4 py-2 rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Modifica Driver
            </button>
            <button
              onClick={hasFile ? handleViewReport : handleGenerateReport}
              disabled={busy}
              className="bg-textPrimary text-white hover:bg-textSecondary px-4 py-2 rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {hasFile ? 'Visualizza Report' : 'Genera Report'}
            </button>
          </>
        )}
      </div>

      <ReportEditorModal
        key={localFile?.id || 'cr-report-editor'}
        isOpen={showEditor}
        onClose={handleCloseEditor}
        reportId={reportId}
        jsonContent={localFile?.json_content}
        reportStatus={reportStatus ?? undefined}
        skipClientNamePrompt
      />
    </>
  );
};

export default ValidateDriversButton;
